import { useCallback, useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";

import { apiClient } from "../api/client.js";
import { useFleetOptions } from "../hooks/useFleetOptions.js";

interface Session {
  id: string;
  venue_name: string;
  device_name: string;
  status: string;
  verification_phrase: string;
  starts_at?: string;
  ends_at?: string;
  created_at: string;
}

interface Pagination {
  page: number;
  per_page: number;
  total: number;
}

interface Venue {
  id: string;
  name: string;
  slug: string;
}

function statusBadge(status: string) {
  switch (status) {
    case "active":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
          Active
        </span>
      );
    case "expired":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          Expired
        </span>
      );
    case "cancelled":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-400">
          Cancelled
        </span>
      );
    case "awaiting_payment":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400">
          Awaiting Payment
        </span>
      );
    default:
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          {status}
        </span>
      );
  }
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(startsAt?: string, endsAt?: string): string {
  if (!startsAt || !endsAt) return "-";
  const ms = new Date(endsAt).getTime() - new Date(startsAt).getTime();
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
}

export function Sessions() {
  const { options: fleetOptions } = useFleetOptions();
  const search = useSearch();
  const [, navigate] = useLocation();
  const params = new URLSearchParams(search);
  const currentPage = Math.max(1, parseInt(params.get("page") ?? "1", 10));
  const selectedVenueId = params.get("venue_id") ?? "";
  const selectedFleetId = params.get("fleet_id") ?? "";

  const [sessions, setSessions] = useState<Session[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchVenues = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/admin/venues"]["get"]({
        parameters: {},
        body: null,
      });
      if (res.code === 200) {
        setVenues(res.body as Venue[]);
      }
    } catch {
      // Ignore
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient["/api/v1/admin/sessions"]["get"]({
        parameters: {
          page: currentPage,
          per_page: 25,
          venue_id: selectedVenueId || undefined,
          fleet_id: selectedFleetId || undefined,
        },
        body: null,
      });
      if (res.code === 200) {
        const body = res.body as { data: Session[]; pagination: Pagination };
        setSessions(body.data);
        setPagination(body.pagination);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedVenueId, selectedFleetId]);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  function setPage(page: number) {
    const p = new URLSearchParams(search);
    p.set("page", String(page));
    navigate("?" + p.toString(), { replace: true });
  }

  function setVenueFilter(venueId: string) {
    const p = new URLSearchParams();
    if (venueId) p.set("venue_id", venueId);
    if (selectedFleetId) p.set("fleet_id", selectedFleetId);
    p.set("page", "1");
    navigate("?" + p.toString(), { replace: true });
  }

  function setFleetFilter(fleetId: string) {
    const p = new URLSearchParams();
    if (selectedVenueId) p.set("venue_id", selectedVenueId);
    if (fleetId) p.set("fleet_id", fleetId);
    p.set("page", "1");
    navigate("?" + p.toString(), { replace: true });
  }

  const totalPages = pagination
    ? Math.max(1, Math.ceil(pagination.total / pagination.per_page))
    : 1;

  return (
    <main className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Sessions</h1>
        <div className="flex items-center gap-3">
          {fleetOptions.length > 1 && (
            <select
              aria-label="Filter by fleet"
              value={selectedFleetId}
              onChange={(e) => setFleetFilter(e.target.value)}
              className="rounded-lg border border-border bg-surface px-3 py-2 text-text-primary text-sm"
            >
              <option value="">All fleets</option>
              {fleetOptions.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          )}
          <select
            aria-label="Filter by venue"
            value={selectedVenueId}
            onChange={(e) => setVenueFilter(e.target.value)}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-text-primary text-sm"
          >
            <option value="">All venues</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <p className="text-text-muted text-sm">Loading sessions...</p>}

      {!loading && sessions.length === 0 && (
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">No sessions found.</p>
        </div>
      )}

      {!loading && sessions.length > 0 && (
        <>
          <div className="grid gap-3">
            {sessions.map((s) => (
              <div
                key={s.id}
                className="bg-surface-raised border border-border rounded-lg p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div>
                    <div className="font-medium text-text-primary">{s.venue_name}</div>
                    <div className="text-sm text-text-muted">{s.device_name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  {statusBadge(s.status)}
                  <div className="text-right">
                    <div className="text-sm text-text-secondary">
                      {s.starts_at ? formatTime(s.starts_at) : formatTime(s.created_at)}
                    </div>
                    <div className="text-xs text-text-muted">
                      {s.ends_at ? formatTime(s.ends_at) : "-"}
                    </div>
                  </div>
                  <div className="text-sm text-text-secondary w-16 text-right">
                    {formatDuration(s.starts_at, s.ends_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {pagination && totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className="px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-text-muted">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
